<template>
  <div id="issue">
    <div id="issue-content">
      <transition appear name="loading-bar">
        <loading-bar v-show="pending" />
      </transition>

      <b-card-header
          v-if="!pending && !item.resolved_at"
          class="mb-0 d-flex align-items-center"
          header-bg-variant="transparent"
      >
        <b-button
            class="ml-auto text-nowrap"
            size="sm"
            variant="success"
            @click="markAsResolved"
        >
          {{ $t('Mark as resolved') }}
          <fa class="ml-1" icon="check" />
        </b-button>
      </b-card-header>


      <b-card-body>
        <b-link v-if="item.subject" :href="item.subject" :append="false">
          <h2>{{ item.title }}</h2>
        </b-link>

        <h2 v-else>
          {{ item.title }}
        </h2>

        <b-card-text v-html="item.text" />

        <small class="text-muted">
          {{ $t('Last update: :date', {date: $moment(item.updated_at).format('lll')}) }}
        </small>

        <template v-if="hasMessages">
          <hr>
          <div class="d-flex flex-column">
            <messages-list-item
                v-for="message in item.messages"
                :key="message.id"
                ref="messages"
                :message="message"
                @click.native="imageLightbox"
            />
          </div>
        </template>
        <hr>

        <new-message
            v-if="!pending"
            :issue-id="+id"
            @input="item.messages.push($event)"
        />
      </b-card-body>
    </div>

    <sidebar id="issue-sidebar">
      <template #default="{collapsed}">
        <template v-if="item.users">
          <b-button
              v-if="collapsed"
              v-b-tooltip.left.html="item.users.map(user => user.name).join('<br>')"
              href="#"
              tabindex="0"
              variant="default"
          >
            <fa fixed-width icon="users" />
          </b-button>
          <div v-else>
            {{ $t('Group') }}
            <a
                v-b-tooltip.left.html="item.users.map(user => user.name).join('<br>')"
                class="d-block text-decoration-none text-body"
                href="#"
                tabindex="0"
            >
              {{ item.group.name }}
            </a>
          </div>

          <hr>
        </template>

        <template v-if="item.responsible_users">
          <template v-if="collapsed">
            <b-button
                id="responsible_users_button"
                ref="responsible_users_button"
                v-b-tooltip.left.html="item.responsible_users.map(user => user.name).join('<br>')"
                variant="default"
            >
              <fa fixed-width icon="user-tie" />
            </b-button>

            <b-popover
                target="responsible_users_button"
                triggers="click blur"
                @hidden="focusRef($refs.responsible_users_button)"
                @shown="focusRef($refs.responsible_users)"
            >
              <b-form-group
                  :label="$t('Responsible')"
                  label-for="responsible_users"
              >
                <resource-select
                    id="responsible_users"
                    ref="responsible_users"
                    v-model="item.responsible_users"
                    multiple
                    resource="users"
                    @input="updateResponsibleUsers"
                />
              </b-form-group>
            </b-popover>
          </template>

          <b-form-group
              v-else
              :label="$t('Responsible')"
              label-for="responsible_users"
          >
            <resource-select
                id="responsible_users"
                v-model="item.responsible_users"
                multiple
                resource="users"
                @input="updateResponsibleUsers"
            />
          </b-form-group>

          <hr>
        </template>
      </template>
    </sidebar>
  </div>
</template>

<script>
import NewMessage from "@/components/issues/NewMessage";
import itemMixin from "@/mixins/itemMixin";
import MessagesListItem from "@/components/issues/MessagesListItem";
import Sidebar from "@/components/Sidebar";
import ResourceSelect from "@/components/selects/ResourceSelect";

export default {
  name: "Issue",
  components: {ResourceSelect, Sidebar, MessagesListItem, NewMessage},
  mixins: [itemMixin],
  props: {
    id: {
      type: [Number, String],
      required: true
    },
  },
  computed: {
    resourceName() {
      return `issues`
    },

    hasMessages() {
      return this.item.messages && this.item.messages.length;
    }
  },
  watch: {
    'item.messages': {
      handler: 'scrollToLastMessage',
      deep: true
    },
  },
  mounted() {
    this.$echo.channel(`issue.${this.id}`)
        .listen('NewMessage', ({message}) => {
          this.item.messages.push(message);
        })
        .listen('IssueResolved', ({issue}) => {
          this.item.resolved_at = issue.resolved_at;
        });
  },
  methods: {
    scrollToLastMessage() {
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.$nextTick(() => {
                if (this.$refs.messages) {
                  let lastMessage = this.$refs.messages.pop();
                  if (lastMessage) {
                    lastMessage.$el.scrollIntoView(false);
                  }
                }
              })
            })
          })
        })
      })
    },

    markAsResolved() {
      this.axios
          .put(`/api/issues/${this.item.id}/resolve`)
          .then(({data}) => {
            this.item.resolved_at = data.resolved_at;
            this.successToast(this.$t('Issue marked as resolved'));
          })
          .catch((e) => {
            if (!e.response) {
              throw e;
            }
          })
    },

    updateResponsibleUsers() {
      this.axios
          .put(`/api/issues/${this.item.id}`, {
            responsible_users: this.item.responsible_users.map(user => user.id)
          })
          .catch((e) => {
            if (!e.response) {
              throw e;
            }
          })
    },

    imageLightbox(event) {
      if (!event.target.matches('.msg img')) return;

      const imgVNode = this.$createElement('b-img', {
        props: {
          src: event.target.src,
          center: true,
          fluid: true,
        }
      });

      // We must pass the generated VNodes as arrays
      this.$bvModal.msgBoxOk([imgVNode], {
        centered: true,
        size: 'xl',
      })
    }
  }
}
</script>

<style>
#issue {
  display: flex;
  position: relative;
}

#issue-content {
  width: 100%;
  padding-right: 61px;
}

#issue-sidebar {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
}

</style>
